"use client";

import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Flip } from "gsap/dist/Flip";
import Button from "../Button";
import { useContent } from "../../content/current";

gsap.registerPlugin(ScrollTrigger, Flip);


export default function Equipo({ isMobile }) {
  const content = useContent();
  const TEAM_MEMBERS = content.team.members;
  const [activeBioIndex, setActiveBioIndex] = useState(null);

  const clearActiveBio = useCallback(() => {
    setActiveBioIndex(null);
  }, []);

  useEffect(() => {
    if (activeBioIndex === null) return undefined;

    const handlePointerDown = (event) => {
      if (event.target?.closest?.(".equipobtnwrap")) return;
      clearActiveBio();
    };

    const handleScroll = () => {
      const equipoSection = document.getElementById("equipo");
      const rect = equipoSection?.getBoundingClientRect();
      if (!rect || rect.bottom <= 0 || rect.top >= window.innerHeight) {
        clearActiveBio();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeBioIndex, clearActiveBio]);
  useLayoutEffect(() => {
    const equipoSection = document.getElementById("equipo");
    const equipoWrap = document.getElementById("equipowrap");
    const equipoItems = gsap.utils.toArray(".equipoitem");
    const equipoItemVeils = gsap.utils.toArray(".equipoitemveil");
    const equipoItemOverlays = gsap.utils.toArray(".equipodeg, .equipobtnwrap");
    const equipoItemButtons = gsap.utils.toArray(".equipobtnwrap");
    const equipoTitle = equipoSection.querySelector(".equipotitle");

    if (!equipoSection || !equipoWrap || !equipoItems.length) {
      return undefined;
    }

    if (isMobile) {
      return undefined;
    }

    let equipoTimeline;
    const OPACITY_PROGRESS_WINDOW = 0.01;
    const setEquipoBackground = gsap.quickSetter(
      equipoSection,
      "backgroundColor",
    );
    const setEquipoBottomLeftRadius = gsap.quickSetter(
      equipoSection,
      "borderBottomLeftRadius",
      "vw",
    );
    const setEquipoBottomRightRadius = gsap.quickSetter(
      equipoSection,
      "borderBottomRightRadius",
      "vw",
    );
    const BORDER_RADIUS_REVEAL_START = 0.86;
    const BORDER_RADIUS_REVEAL_END = 1;
    const MAX_BORDER_RADIUS = 4;
    const VISIBILITY_THRESHOLD = 0.01;
    const OVERLAY_REVEAL_START = 0.18;
    const OVERLAY_REVEAL_END = 0.28;
    const FLIP_INTERACTIVE_THRESHOLD = 0.65;
    const TITLE_REVEAL_START = 0.45;
    const TITLE_REVEAL_END = 0.85;
    const getThresholdProgress = (value, start, end) => {
      if (value <= start) return 0;
      if (value >= end) return 1;
      return (value - start) / (end - start);
    };
    const showEquipo = () => {
      gsap.set(equipoSection, { visibility: "visible" });
    };
    const hideEquipo = () => {
      gsap.set(equipoSection, { visibility: "hidden" });
    };
    const syncEquipoVisibility = (progress) => {
      if (progress <= VISIBILITY_THRESHOLD) {
        hideEquipo();
      } else {
        showEquipo();
      }
    };
    const syncEquipoScrollRadius = (progress) => {
      const radiusProgress = getThresholdProgress(
        progress,
        BORDER_RADIUS_REVEAL_START,
        BORDER_RADIUS_REVEAL_END,
      );
      const radius = radiusProgress * MAX_BORDER_RADIUS;
      setEquipoBottomLeftRadius(radius);
      setEquipoBottomRightRadius(radius);
    };

    const updateEquipoProgress = (progress) => {
      syncEquipoVisibility(progress);
      syncEquipoScrollRadius(progress);
      const revealProgress = Math.min(progress / OPACITY_PROGRESS_WINDOW, 1);
      const overlayProgress = getThresholdProgress(
        progress,
        OVERLAY_REVEAL_START,
        OVERLAY_REVEAL_END,
      );
      setEquipoBackground(`rgba(255,255,255,${revealProgress})`);
      equipoTimeline?.progress(progress);
      gsap.set(equipoItemOverlays, { opacity: overlayProgress });
      gsap.set(equipoItemButtons, {
        display: overlayProgress === 0 ? "none" : "",
        pointerEvents: progress >= FLIP_INTERACTIVE_THRESHOLD ? "" : "none",
      });
      if (equipoTitle) {
        gsap.set(equipoTitle, {
          opacity: getThresholdProgress(progress, TITLE_REVEAL_START, TITLE_REVEAL_END),
        });
      }
    };

    const applyAbsoluteLayout = () => {
      equipoWrap.classList.remove("equipo-relative-layout");

      equipoItems.forEach((item) => {
        gsap.set(item, {
          left: "50%",
          top: "50%",
          xPercent: -50,
          yPercent: -50,
          x: 0,
          y: 0,
          rotation: 0,
        });
      });
    };

    const buildFlipTween = (progress = 0) => {
      equipoTimeline?.kill();
      hideEquipo();
      applyAbsoluteLayout();

      const state = Flip.getState(equipoItems);
      equipoWrap.classList.add("equipo-relative-layout");
      gsap.set(equipoItems, {
        clearProps: "top,left,xPercent,yPercent,x,y,rotation",
      });
      gsap.set(equipoItemVeils, { opacity: 1 });
      gsap.set(equipoItemOverlays, { opacity: 0 });
      gsap.set(equipoItemButtons, { display: "none" });

      equipoTimeline = gsap.timeline({
        paused: true,
      });

      equipoTimeline.add(
        Flip.from(state, {
          duration: 1,
          ease: "power2.inOut",
          stagger: 0.06,
          simple: true,
        }),
        0,
      );

      if (equipoItemVeils.length) {
        equipoTimeline.to(
          equipoItemVeils,
          {
            opacity: 0,
            duration: 1,
            ease: "power2.inOut",
            stagger: 0.06,
          },
          0,
        );
      }
      equipoTimeline.pause(0).progress(progress);
      gsap.set(equipoItemButtons, {
        pointerEvents: progress >= FLIP_INTERACTIVE_THRESHOLD ? "" : "none",
      });
    };

    buildFlipTween();
    hideEquipo();

    const trigger = ScrollTrigger.create({
      trigger: equipoSection,
      start: "top top",
      end: "+=100%",
      pin: true,
      scrub: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        updateEquipoProgress(self.progress);
      },
      onRefreshInit: (self) => {
        buildFlipTween(self.progress);
        syncEquipoVisibility(self.progress);
        syncEquipoScrollRadius(self.progress);
      },
      onRefresh: (self) => {
        if (!equipoTimeline) return;
        updateEquipoProgress(self.progress);
      },
    });

    return () => {
      try {
        trigger.kill(true);
      } catch (_) {
        // pin spacer removeChild races with React DOM cleanup
      }
      equipoTimeline?.kill();
      equipoWrap.classList.remove("equipo-relative-layout");
      setEquipoBottomLeftRadius(0);
      setEquipoBottomRightRadius(0);
      if (equipoSection?.isConnected) {
        gsap.set(equipoSection, { clearProps: "visibility,backgroundColor,borderBottomLeftRadius,borderBottomRightRadius" });
      }
      if (equipoItems?.length && equipoItems[0]?.isConnected) {
        gsap.set(equipoItems, { clearProps: "all" });
        gsap.set(equipoItemVeils, { clearProps: "opacity" });
        gsap.set(equipoItemOverlays, { clearProps: "opacity,display,pointerEvents" });
      }
      if (equipoTitle?.isConnected) {
        gsap.set(equipoTitle, { clearProps: "opacity" });
      }
    };
  }, [isMobile]);

  const handleBioButtonClick = (index) => {
    setActiveBioIndex((current) => (current === index ? null : index));
  };

  if (isMobile) return null;

  // Wrapper con display:contents (no genera caja, cero impacto en layout). GSAP
  // pinnea #equipo y lo envuelve en un pin-spacer DENTRO de este wrapper; así
  // React solo elimina el wrapper (cuyo padre nunca cambia) y nunca intenta un
  // removeChild sobre la section reparentada -> evita el NotFoundError en resize.
  return (
    <div style={{ display: "contents" }}>
      <section id="equipo" onClick={clearActiveBio}>
        <h2 className="equipotitle" dangerouslySetInnerHTML={{ __html: content.team.title }} />
        <div id="equipowrap">
          {TEAM_MEMBERS.map((member, index) => (
            <div key={member.image} className={`equipoitem${activeBioIndex === index ? " is-active" : ""}`}>
              <div className="equipoitemcont">
                <img
                  src={member.image}
                  alt={member.name}
                  width="500"
                  height="500"
                  decoding="async"
                />
                <div className="equipodeg"></div>
                <div className="equipobtnwrap" onClick={(event) => event.stopPropagation()}>
                  <Button onClick={() => handleBioButtonClick(index)}>{member.name}</Button>
                </div>
                <div className="equipobio">
                  <span className="equipobiorole">
                    <strong>{member.role}</strong>
                  </span>
                  <p>{member.bio}</p>
                </div>
                <div className="equipoitemveil" aria-hidden="true"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
